import type { AuthorProfile, CommunityTopic } from './types';
import { loadMarketProfile, profileToAuthorProfile } from './marketProfile/marketProfileStore';

export function formatForumDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);
  if (diffHours < 24) {
    return new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' }).format(date);
  }
  if (diffHours < 24 * 7) {
    return new Intl.DateTimeFormat('ru-RU', { weekday: 'short', hour: '2-digit', minute: '2-digit' }).format(date);
  }
  return new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short' }).format(date);
}

export function formatForumDateFull(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export function authorLine(author: AuthorProfile): string {
  const parts = [author.sector, author.roleLevel, author.company]
    .map((value) => value?.trim())
    .filter((value): value is string => Boolean(value && value !== '—'));
  return parts.length > 0 ? parts.join(' · ') : '—';
}

export function authorDisplayFields(author: AuthorProfile) {
  return {
    pseudonym: author.pseudonym,
    industry: author.industry?.trim() || 'Инвестиционный рынок',
    sector: author.sector,
    position: author.roleLevel,
    company: author.company?.trim() || null,
  };
}

export function topicMetaLine(topic: CommunityTopic): string {
  const parts: string[] = [`${topic.replyCount} ${pluralReplies(topic.replyCount)}`];
  if (topic.author.isAnonymous) parts.push('анонимно');
  if (topic.hasSummary) parts.push('есть сводка');
  if (topic.status === 'closed') parts.push('закрыта');
  if (topic.status === 'pinned') parts.push('закреплена');
  return parts.join(' · ');
}

function pluralReplies(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'ответ';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'ответа';
  return 'ответов';
}

export function normalizeAuthorKey(email: string | null | undefined): string {
  return (email ?? '').trim().toLowerCase();
}

export function isOwnCommunityMessage(
  authorKey: string | undefined,
  userEmail: string | null | undefined,
): boolean {
  const key = normalizeAuthorKey(userEmail);
  if (!key || !authorKey) return false;
  return authorKey === key;
}

export function adminMemberProfile(): AuthorProfile {
  return {
    pseudonym: 'Команда datacircle',
    industry: 'Инвестиционный рынок',
    sector: 'Модерация',
    roleLevel: 'Администратор',
  };
}

export function defaultMemberProfile(useAnonymous: boolean): AuthorProfile {
  if (useAnonymous) {
    return {
      pseudonym: 'Анонимный участник',
      industry: 'Инвестиционный рынок',
      sector: '—',
      roleLevel: 'Участник',
      isAnonymous: true,
    };
  }
  return {
    pseudonym: 'Участник datacircle',
    industry: 'Инвестиционный рынок',
    sector: 'Прямые инвестиции',
    roleLevel: 'Специалист',
    experienceRange: '5–7 лет',
  };
}

/** Автор сообщения/темы из сохранённого профиля рынка */
export function memberAuthorFromProfile(useAnonymous: boolean, userEmail?: string | null): AuthorProfile {
  if (useAnonymous) return defaultMemberProfile(true);
  const profile = loadMarketProfile(userEmail).userProfile;
  if (profile.data.pseudonym.trim() || profile.data.firstName.trim()) {
    return profileToAuthorProfile(profile);
  }
  return defaultMemberProfile(false);
}

export const SECTION_ICONS = [
  'fa-folder',
  'fa-coins',
  'fa-layer-group',
  'fa-chart-line',
  'fa-route',
  'fa-bullhorn',
  'fa-circle-question',
  'fa-book',
  'fa-shield-halved',
  'fa-compass',
] as const;
